import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import 'core-js/modules/es6.array.find-index.js';
import 'core-js/modules/es6.array.slice.js';
import 'core-js/modules/es6.array.map.js';
import 'core-js/modules/es6.object.to-string.js';
import 'core-js/modules/es6.regexp.to-string.js';
import React__default, { useMemo, useState, useRef } from 'react';
import { MenuItem, Menu, Button } from '@blueprintjs/core';
import { Select, isCreateNewItem } from '@blueprintjs/select';
import { List } from 'react-virtualized';
import { Icon } from '../icon/icon.js';
import { ChevronDown20 } from '@carbon/icons-react';

var DROPDOWN_WIDTH = 100;
var DROPDOWN_HEIGHT = 200;
var ROW_HEIGHT = 20;

var isItemType = function isItemType(item) {
  if (item === null || isCreateNewItem(item)) {
    return false;
  }

  return true;
};

var RenderMenu = function RenderMenu(itemsEqual) {
  return function (_ref) {
    var items = _ref.items,
        itemsParentRef = _ref.itemsParentRef,
        renderItem = _ref.renderItem,
        activeItem = _ref.activeItem;
    return /*#__PURE__*/React__default.createElement(Menu, {
      ulRef: itemsParentRef,
      style: {
        height: "".concat(DROPDOWN_HEIGHT, "px"),
        width: "".concat(DROPDOWN_WIDTH, "px"),
        minWidth: 0,
        padding: 0
      }
    }, /*#__PURE__*/React__default.createElement(List, {
      scrollToIndex: isItemType(activeItem) ? items.findIndex(function (item) {
        return itemsEqual(item, activeItem);
      }) : 0,
      scrollToAlignment: "start",
      rowCount: items.length,
      rowRenderer: function rowRenderer(_ref2) {
        var key = _ref2.key,
            index = _ref2.index,
            style = _ref2.style;
        return /*#__PURE__*/React__default.createElement("div", {
          key: key,
          style: style
        }, renderItem(items[index], index));
      },
      width: DROPDOWN_WIDTH,
      height: DROPDOWN_HEIGHT,
      rowHeight: ROW_HEIGHT
    }));
  };
};

var findLabelWithValue = function findLabelWithValue(options, selectedValue) {
  var index = options.findIndex(function (dateObject) {
    return dateObject.value == selectedValue;
  });
  return options[index].label;
};

function LazyYearSelect(_ref3) {
  var onYearChange = _ref3.onYearChange,
      date = _ref3.date,
      minYear = _ref3.minYear,
      maxYear = _ref3.maxYear;
  var displayYear = date.getFullYear();
  var years = useMemo(function () {
    var years = [minYear];

    for (var year = minYear + 1; year <= maxYear; ++year) {
      years.push(year);
    } // allow out-of-bounds years but disable the option. this handles the Dec 2016 case in #391.


    if (displayYear > maxYear) {
      years.push({
        value: displayYear,
        disabled: true
      });
    }

    return years;
  }, [displayYear, minYear, maxYear]);
  return /*#__PURE__*/React__default.createElement(LazyPeriodSelect, {
    options: years,
    onChange: onYearChange,
    selected: displayYear,
    type: "year",
    initialActiveItem: displayYear,
    itemsEqual: function itemsEqual(a, b) {
      return a === b;
    }
  });
}
var LazyMonthSelection = function LazyMonthSelection(_ref4) {
  var date = _ref4.date,
      maxDate = _ref4.maxDate,
      minDate = _ref4.minDate,
      months = _ref4.months,
      onMonthChange = _ref4.onMonthChange;
  var displayYear = date.getFullYear();
  var displayMonth = date.getMonth(); // build the list of available months, limiting based on minDate and maxDate as necessary

  var startMonth = displayYear === minDate.getFullYear() ? minDate.getMonth() : 0;
  var endMonth = displayYear === maxDate.getFullYear() ? maxDate.getMonth() + 1 : undefined;
  var monthOptionElements = useMemo(function () {
    return months.map(function (month, i) {
      return {
        label: month,
        value: i
      };
    }).slice(startMonth, endMonth);
  }, [months, startMonth, endMonth]);
  return /*#__PURE__*/React__default.createElement(LazyPeriodSelect, {
    options: monthOptionElements,
    onChange: onMonthChange,
    selected: displayMonth,
    type: "month",
    initialActiveItem: {
      label: months[displayMonth],
      value: displayMonth
    },
    itemsEqual: function itemsEqual(a, b) {
      return a.value === b.value;
    }
  });
};

var LazyPeriodSelect = function LazyPeriodSelect(_ref5) {
  var options = _ref5.options,
      onChange = _ref5.onChange,
      selected = _ref5.selected,
      type = _ref5.type,
      initialActiveItem = _ref5.initialActiveItem,
      itemsEqual = _ref5.itemsEqual;

  var _useState = useState(initialActiveItem),
      _useState2 = _slicedToArray(_useState, 2),
      activeItem = _useState2[0],
      setActiveItem = _useState2[1];

  var buttonRef = useRef(null);
  return /*#__PURE__*/React__default.createElement("div", {
    onKeyDown: function onKeyDown(event) {
      return event.stopPropagation();
    },
    onKeyUp: function onKeyUp(event) {
      return event.stopPropagation();
    }
  }, /*#__PURE__*/React__default.createElement(Select, {
    activeItem: activeItem,
    className: "fullwidth",
    items: options,
    itemsEqual: itemsEqual,
    itemRenderer: function itemRenderer(item, _ref6) {
      var handleClick = _ref6.handleClick;
      return /*#__PURE__*/React__default.createElement(MenuItem, {
        key: item.toString() // px are used to match the row height provided as an arg to the react virtualized List
        // font size is locked to ensure the text doesn't overflow the row container with a fixed height
        ,
        style: {
          height: "".concat(ROW_HEIGHT, "px"),
          margin: 0,
          padding: "0 5px",
          fontSize: "14px"
        },
        disabled: typeof item !== "number" ? item.disabled : false,
        text: typeof item !== "number" ? item.label : item,
        onClick: handleClick,
        active: itemsEqual(item, activeItem),
        shouldDismissPopover: false
      });
    },
    onItemSelect: function onItemSelect(item) {
      onChange(typeof item === "number" ? item : typeof item.value === "number" ? item.value : parseInt(item.value, 10));
    },
    filterable: false,
    popoverProps: {
      minimal: true,
      usePortal: false,
      captureDismiss: true,
      position: type === "month" ? "bottom-left" : "bottom-right",
      onClosed: function onClosed() {
        var _buttonRef$current;

        return (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.blur();
      },
      onOpened: function onOpened() {
        var _buttonRef$current2;

        return (_buttonRef$current2 = buttonRef.current) === null || _buttonRef$current2 === void 0 ? void 0 : _buttonRef$current2.focus();
      }
    },
    itemListRenderer: RenderMenu(itemsEqual),
    onActiveItemChange: function onActiveItemChange(item) {
      return setActiveItem(item !== null && item !== void 0 ? item : initialActiveItem);
    }
  }, /*#__PURE__*/React__default.createElement(Button, {
    elementRef: buttonRef,
    name: type,
    fill: type === "month",
    text: type === "month" ? findLabelWithValue(options, selected) : selected,
    rightIcon: /*#__PURE__*/React__default.createElement(Icon, null, /*#__PURE__*/React__default.createElement(ChevronDown20, null)),
    alignText: "left",
    minimal: true,
    style: {
      minHeight: 0,
      display: "flex",
      padding: "0 0.25rem",
      position: "relative",
      zIndex: 999
    }
  })));
};

export { LazyMonthSelection, LazyYearSelect as default };
//# sourceMappingURL=lazyYearSelect.js.map
